import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, Order, ReceiptItem } from "../api";
import { formatVND, formatNumber, formatTime, paymentLabel } from "../utils";
import { GlassPanel, KpiCard, Badge } from "../components";
import { setBackButton } from "../tg";

export default function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [items, setItems] = useState<ReceiptItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    setLoading(true);
    api
      .order(id)
      .then((r) => {
        if (!alive) return;
        setOrder(r.order);
        setItems(r.items);
      })
      .catch((e) => alive && setError(e.message))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [id]);

  // Telegram BackButton wires to nav(-1)
  useEffect(() => {
    const cleanup = setBackButton(() => nav(-1));
    return cleanup;
  }, [nav]);

  if (loading) return <div className="loading">Đang tải…</div>;
  if (error) return <div className="error">⚠️ {error}</div>;
  if (!order) return <div className="empty">Không tìm thấy đơn</div>;

  const subtotal = items.reduce((sum, it) => sum + it.gross_total, 0);

  return (
    <>
      <button onClick={() => nav(-1)} className="btn btn-ghost" style={{ marginBottom: 16 }}>
        <span className="material-symbols-outlined">arrow_back</span> Quay lại
      </button>

      <div className="kpi-grid">
        <KpiCard label={`#${order.receipt_number}`} value={formatVND(order.total_money)} note={formatTime(order.receipt_date)} dotColor="ok" />
        <KpiCard label="Thanh toán" value={paymentLabel(order.payment_type)} note={order.customer_id ? "Có khách" : "Khách lẻ"} dotColor="blue" />
        <KpiCard label="Số sản phẩm" value={formatNumber(items.length)} note="line items" dotColor="" />
        <KpiCard label="Giảm giá" value={formatVND(order.total_discount)} note={order.total_discount > 0 ? "đã áp dụng" : "không"} dotColor={order.total_discount > 0 ? "warn" : ""} />
      </div>

      <GlassPanel title="Sản phẩm trong đơn" badge={`${items.length} dòng`} badgeVariant="info">
        <div className="list">
          {items.map((it) => (
            <div className="list-item" key={it.id}>
              <div>
                <div className="name">{it.item_name}</div>
                <div className="meta">
                  {formatNumber(it.quantity)} × {formatVND(it.price)}
                  {it.sku ? ` · ${it.sku}` : ""}
                </div>
              </div>
              <div className="right">
                <div className="amount">{formatVND(it.total_money)}</div>
                {it.total_discount > 0 && <div className="meta">−{formatVND(it.total_discount)}</div>}
              </div>
            </div>
          ))}
        </div>
      </GlassPanel>

      <GlassPanel title="Chi tiết thanh toán" className="mt-3">
        <div className="list">
          <div className="list-item">
            <span className="meta">Tạm tính</span>
            <span>{formatVND(subtotal)}</span>
          </div>
          {order.total_discount > 0 && (
            <div className="list-item">
              <span className="meta">Giảm giá</span>
              <span style={{ color: "var(--accent-pink)" }}>−{formatVND(order.total_discount)}</span>
            </div>
          )}
          {order.total_tax > 0 && (
            <div className="list-item">
              <span className="meta">Thuế</span>
              <span>{formatVND(order.total_tax)}</span>
            </div>
          )}
          <div className="list-item" style={{ background: "var(--bg-elevated)" }}>
            <span style={{ fontWeight: 700 }}>Tổng cộng</span>
            <span className="amount" style={{ fontSize: 16 }}>{formatVND(order.total_money)}</span>
          </div>
        </div>
        <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Badge variant="ok">{order.receipt_type}</Badge>
          {order.cancelled_at && <Badge variant="bad">Đã huỷ</Badge>}
          {order.refund_for && <Badge variant="warn">Hoàn cho #{order.refund_for}</Badge>}
        </div>
      </GlassPanel>
    </>
  );
}
